export default function HowItWorks() {
  const steps = [
    {
      number: '01',
      icon: 'ri-map-pin-line',
      title: 'Enter Your Postcode',
      description: 'Tell us where you need the service and we\'ll check availability in your area',
      gradient: 'from-orange-500 to-orange-600',
      bgGradient: 'from-orange-50 to-orange-100'
    },
    {
      number: '02',
      icon: 'ri-tools-line',
      title: 'Select Your Service',
      description: 'Choose from our range of professional trade services and describe your requirements',
      gradient: 'from-teal-500 to-teal-600',
      bgGradient: 'from-teal-50 to-teal-100'
    },
    {
      number: '03',
      icon: 'ri-secure-payment-line',
      title: 'Book & Pay',
      description: 'Get matched with a verified provider and make a secure payment',
      gradient: 'from-blue-500 to-blue-600',
      bgGradient: 'from-blue-50 to-blue-100'
    },
    {
      number: '04',
      icon: 'ri-checkbox-circle-line',
      title: 'Job Complete',
      description: 'Track your job in real-time and rate your experience when finished',
      gradient: 'from-green-500 to-green-600',
      bgGradient: 'from-green-50 to-green-100'
    }
  ];

  return (
    <section className="py-20 sm:py-24 lg:py-32 bg-gradient-to-br from-slate-50 via-white to-slate-50 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-orange-100 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-100 rounded-full filter blur-3xl opacity-20 translate-x-1/2 translate-y-1/2"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-orange-500 to-teal-500 rounded-full mb-6">
            <span className="text-sm font-semibold text-white tracking-wide">SIMPLE PROCESS</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            How It Works
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Get started in four simple steps and experience hassle-free service booking
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 relative">
          {/* Animated Connecting Lines - Desktop Only */}
          <div className="hidden lg:block absolute top-32 left-[12.5%] right-[12.5%] h-1">
            <div className="relative h-full">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-200 via-teal-200 to-green-200 rounded-full"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-teal-500 to-green-500 rounded-full animate-pulse opacity-50"></div>
            </div>
          </div>

          {steps.map((step, index) => (
            <div
              key={index}
              className="relative group flex"
            >
              {/* Card */}
              <div className={`relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-gray-100 hover:-translate-y-2 flex flex-col w-full group-hover:border-transparent`}>
                {/* Clear Gradient Border Effect on Hover */}
                <div className={`absolute -inset-[2px] bg-gradient-to-br ${step.gradient} rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10`}></div>
                <div className="absolute inset-0 bg-white rounded-[22px] z-0"></div>
                
                {/* Step Number Badge */}
                <div className="absolute -top-4 -right-4 w-16 h-16 flex items-center justify-center z-10">
                  <div className={`absolute inset-0 bg-gradient-to-br ${step.gradient} rounded-2xl rotate-12 group-hover:rotate-45 transition-transform duration-500`}></div>
                  <span className="relative text-2xl font-bold text-white z-10">{step.number}</span>
                </div>

                {/* Icon Container */}
                <div className="relative mb-6 z-10">
                  <div className={`w-24 h-24 mx-auto flex items-center justify-center bg-gradient-to-br ${step.bgGradient} rounded-2xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-md`}>
                    <div className={`w-20 h-20 flex items-center justify-center bg-gradient-to-br ${step.gradient} rounded-xl group-hover:rounded-2xl transition-all duration-500`}>
                      <i className={`${step.icon} text-4xl text-white`}></i>
                    </div>
                  </div>
                  
                  {/* Decorative Dots */}
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                    <div className={`w-2 h-2 bg-gradient-to-br ${step.gradient} rounded-full group-hover:scale-150 transition-transform duration-300`}></div>
                    <div className={`w-2 h-2 bg-gradient-to-br ${step.gradient} rounded-full group-hover:scale-150 transition-transform duration-300 delay-75`}></div>
                    <div className={`w-2 h-2 bg-gradient-to-br ${step.gradient} rounded-full group-hover:scale-150 transition-transform duration-300 delay-150`}></div>
                  </div>
                </div>

                {/* Content */}
                <div className="text-center mt-8 flex-grow relative z-10">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 group-hover:bg-gradient-to-r group-hover:from-orange-500 group-hover:to-teal-500 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                    {step.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Bottom Accent Line */}
                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${step.gradient} rounded-b-3xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 z-10`}></div>
              </div>

              {/* Arrow Connector - Desktop Only */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-32 -right-3 z-20">
                  <div className={`w-6 h-6 flex items-center justify-center bg-gradient-to-br ${step.gradient} rounded-full shadow-lg`}>
                    <i className="ri-arrow-right-line text-white text-sm"></i>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <button className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-orange-500 to-teal-500 text-white font-semibold rounded-full shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap">
            <span>Get Started Now</span>
            <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
